import * as Handsontable from "handsontable";
import { GridSettings } from "handsontable";
import "jquery-ui/ui/widgets/droppable";
import { C } from "../common/component";
import { Helper } from "../common/helper";
import { TimesheetSelectedCells } from "./timesheet-right-table-configurer";
import { TimesheetChange, TimesheetRightTableDataManager } from "./timesheet-right-table-data-manager";
import { TableHelper } from "../common/table-helper";
import { TimesheetCommentsManager } from "./timesheet-comments-manager";
import { TimesheetCellButton, TimesheetRightTableColumn } from "./timesheet-models";
import { Form, FormDisplayer } from "../forms/form";
import { Requester } from "../common/requester";
import { Loader } from "../common/loader";
import { MessageDisplayer } from "../common/message-displayer";
import { EditableTableCalculator } from "../common/editable-table";
import { DomainHelper } from "../common/domain-helper";
import { Txt } from "../common/text";
import { HotContextMenuAdapter } from "./hot-context-menu-adapter";
import { ContextMenuItem } from "./custom-context-menu";

class CustomEditor extends Handsontable.editors.TextEditor
{
    prepare(row: number, col: number, prop: string | number, td: HTMLElement, originalValue: any, cellProperties: GridSettings): void
    {
        if (originalValue && originalValue.indexOf("^*") !== -1)
        {
            originalValue = originalValue.substring(0, originalValue.lastIndexOf("^*"));
        }
        super.prepare(row, col, prop, td, originalValue, cellProperties);
    }
}

export class TimesheetRightTable
{
    private readonly doNotTriggerSave = "doNotTriggerSave";

    private readonly columns: TimesheetRightTableColumn[];
    private readonly borderRows: number[];
    private readonly rowHeight: number;
    private readonly rowHeaderWidth: number;
    private readonly width: number;
    private readonly multiCellButtons: TimesheetCellButton[];
    private readonly isCompact: boolean;
    private readonly HideRightTimeSheetShortcuts: boolean;
    private readonly DisableDeletingMoreThanOneItem: boolean;
    private dataManager: TimesheetRightTableDataManager;
    private customContextMenu?: HotContextMenuAdapter;

    private headerHeight: number;
    private rightTable: any;
    private hot: Handsontable;
    private additionalHeader: any;
    private touchStartY = 0;
    private touchStartX = 0;
    private isScrolling = false;
    private scrollThreshold = 10;

    private boundKeyHandler?: (e: KeyboardEvent) => void;

    constructor(columns: TimesheetRightTableColumn[],
                borderRows: number[],
                rowHeight: number,
                rowHeaderWidth: number,
                width: number,
                multiCellButtons: TimesheetCellButton[],
                isCompact: boolean,
                HideRightTimeSheetShortcuts: boolean,
                DisableDeletingMoreThanOneItem: boolean)
    {
        this.columns = columns;
        this.borderRows = borderRows;
        this.rowHeight = rowHeight;
        this.rowHeaderWidth = rowHeaderWidth;
        this.width = width;
        this.multiCellButtons = multiCellButtons;
        this.isCompact = isCompact;
        this.HideRightTimeSheetShortcuts = HideRightTimeSheetShortcuts;
        this.DisableDeletingMoreThanOneItem = DisableDeletingMoreThanOneItem;
    }

    appendTo(parent,
             dataManager: TimesheetRightTableDataManager,
             commentsManager: TimesheetCommentsManager,
             calculator: EditableTableCalculator)
    {
        this.dataManager = dataManager;

        const rightColumn = C.div.addClass(`col-xs-${12 - this.width} schedule-col`)
            .css("height", C.leftTable.height())
            .css("overflow", "hidden")
            .css("flex", Helper.isMobileScreen() ? "1 1 auto" : "")
            .appendTo(parent);
        this.rightTable = C.table.addClass("table table-bordered table-sm schedule-table")
            .appendTo(rightColumn);

        const startDate = Helper.stringToDate(this.columns[0].Date);
        const endDate = Helper.stringToDate(this.columns[this.columns.length - 1].Date);
        const headerRow = TableHelper.createMonthsHeaderRowFromDates(startDate, endDate);
        const data = dataManager.getDisplayableData();

        this.additionalHeader = null;
        if (this.columns[0].AdditionalCell)
        {
            this.additionalHeader = C.tr.css("height", "24px");
            C.th.appendTo(this.additionalHeader);
            for (const column of this.columns)
            {
                calculator.addCell(C.div.appendTo(C.th.appendTo(this.additionalHeader)), column.AdditionalCell);
            }
        }

        const handsontableConfig: any = {
            editor: CustomEditor,
            autoWrapRow: true,
            beforeRender: () => C.hotTableHeader.empty(),
            afterRender: () =>
            {
                const headers = C.hotTableHeader;
                headerRow.prependTo(headers);
                for (let i = 0; i < this.columns.length; i++)
                {
                    if (this.columns[i].Tooltip)
                    {
                        for (const header of <any>headers)
                        {
                            const headerObject = C.create(header).children()[1];
                            const children = C.create(headerObject).children();
                            C.create(C.create(children[i + 1]).children()[0]).tooltip({
                                title: this.columns[i].Tooltip
                            });
                        }
                    }
                }
                if (this.additionalHeader)
                {
                    this.additionalHeader.appendTo(headers);
                    if (DomainHelper.getDomain() === "yolo")
                    {
                        for (const header of <any>headers)
                        {
                            const headerObject = C.create(header).children()[2];
                            const children = C.create(headerObject).children();
                            for (let i = 0; i < this.columns.length; i++)
                            {
                                C.create(C.create(children[i + 1]).children()[0]).tooltip({
                                    title: () =>
                                    {
                                        const shifts = {};
                                        for (const row of dataManager.getData().getAllRows())
                                        {
                                            const cell = row.Cells[i];
                                            const shortcut = dataManager.getShortcuts().tryGetByAbbreviation(cell.Value);
                                            if (shortcut && shortcut.Type !== "VacationType")
                                            {
                                                if (!shifts[cell.Value])
                                                {
                                                    shifts[cell.Value] = { count: 0, shortcut: shortcut };
                                                }
                                                shifts[cell.Value].count += 1;
                                            }
                                        }
                                        const array = Object.keys(shifts).map(key => [key, shifts[key]]);
                                        array.sort((first, second) =>
                                            first[1].shortcut.Start + first[1].shortcut.End < second[1].shortcut.Start + second[1].shortcut.End ? -1 : 1);
                                        return array.map(x => x[0] + " " + x[1].count).join(", ");
                                    }
                                });
                            }
                        }
                    }
                }
            },
            currentHeaderClassName: "",
            viewportColumnRenderingOffset: 1000,
            rowHeights: this.rowHeight,
            maxRows: data.length,
            maxCols: this.columns.length,
            colHeaders: (columnNumber) => dataManager.getColumnHeader(columnNumber),
            rowHeaders: (rowNumber) => dataManager.getRowHeader(rowNumber, commentsManager),
            rowHeaderWidth: (this.HideRightTimeSheetShortcuts && Helper.isMobileScreen() === false) ? 0 : this.rowHeaderWidth,
            data: data,
            cells: (rowNumber, columnNumber) => ({ readOnly: !dataManager.isCellEditable(rowNumber, columnNumber) }),
            renderer: (instance, td, row, col, prop, value, cellProperties) =>
            {
                Handsontable.renderers.TextRenderer.apply(this, [instance, td, row, col, prop, value, cellProperties]);
                const cell = C.create(td);
                const borderColor = this.columns[col].BorderColor;
                if (borderColor)
                {
                    cell.css("border-bottom", "1px solid " + borderColor);
                    if (col === 0 || this.columns[col - 1].BorderColor !== borderColor)
                    {
                        cell.css("border-left", "2px solid " + borderColor);
                    }
                    if (col === this.columns.length - 1 || this.columns[col + 1].BorderColor !== borderColor)
                    {
                        cell.css("border-right", "2px solid " + borderColor);
                    }
                }
                if (this.borderRows.includes(row))
                {
                    cell.css("border-bottom", "1px solid black");
                }
                dataManager.formatCell(row, col, cell, value);
                commentsManager.configureComment(cell, row, col);
                return td;
            },
            afterChange: (changes, source) =>
            {
                if (source !== "loadData" && source !== this.doNotTriggerSave)
                {
                    const convertedChanges: TimesheetChange[] = [];
                    for (const change of changes)
                    {
                        // Workaround for handsontable bug https://github.com/handsontable/handsontable/issues/5477
                        if (change[3] && change[3].endsWith("\r"))
                        {
                            change[3] = change[3].substring(0, change[3].length - 1);
                        }
                        convertedChanges.push(new TimesheetChange(change[0], change[1], change[2], change[3]));
                    }
                    dataManager.save(convertedChanges, change =>
                        this.hot.setDataAtCell(change.rowNumber, change.columnNumber, change.oldValue, this.doNotTriggerSave));
                }
            },
            beforeChange: (changes, source) =>
            {
                if (!changes || !this.hot)
                {
                    return;
                }

                const selected = this.hot.getSelected();
                if (!selected || selected.length === 0)
                {
                    return;
                }

                const isMultiSelection = selected.some(sel => sel[0] !== sel[2] || sel[1] !== sel[3]);

                const isDeleteAction = changes.every(c => c[3] === "" || c[3] === null);

                if (isMultiSelection && isDeleteAction && this.DisableDeletingMoreThanOneItem)
                {
                    MessageDisplayer.showError(Txt.timesheetMassDeleteNotAllowed);
                    return false;
                }
            },
            contextMenu: { items: {} }
        };

        if (this.isCompact)
        {
            handsontableConfig.colWidths = 40;
        }

        this.hot = new Handsontable(this.rightTable[0], handsontableConfig);

        // disable built-in context menu and attach custom one
        const contextMenuPlugin = this.hot.getPlugin("contextMenu");
        if (contextMenuPlugin && typeof contextMenuPlugin.disablePlugin === "function")
        {
            contextMenuPlugin.disablePlugin();
            this.hot.addHook("afterRender", () =>
            {
                this.hot.getPlugin("contextMenu")?.disablePlugin();
            });
        }

        this.customContextMenu = new HotContextMenuAdapter(
            commentsManager,
            dataManager,
            dataManager.getShortcuts(),
            this.multiCellButtons
        );

        this.customContextMenu.attachToHot(this.hot);

        this.attachKeyListener();
        this.setupMobileScrollBehavior();

        this.setFixedHeader();

        this.rightTable.droppable({
            accept: ".ui-draggable",
            drop: (event, ui) =>
            {
                ui.helper.hide();
                const destination = C.create(<HTMLElement>document.elementFromPoint(event.clientX, event.clientY));
                const tr = destination.closest("tr");
                const tbody = tr.closest("tbody");
                const col = tr.children().index(destination) - 1;
                const row = tbody.children().index(tr);
                if (col >= 0 && row >= 0 && dataManager.isCellEditable(row, col))
                {
                    this.hot.setDataAtCell(row, col, ui.draggable.text());
                }
            },
        });
    }

    setHeaderHeight(height: number)
    {
        if (this.additionalHeader)
        {
            height -= 24;
        }
        if (this.headerHeight !== height)
        {
            (<any>this.hot).updateSettings({ columnHeaderHeight: height });
            this.headerHeight = height;
        }
    }

    setDataAtCellsWithoutSave(changes: any[][])
    {
        this.hot.setDataAtCell(<any>changes, this.doNotTriggerSave);
    }

    render()
    {
        this.hot.render();
    }

    setFixedHeader()
    {
        const header = C.hotClone;
        const rightTable = C.rightTable;
        const windowQuery = C.window;

        windowQuery.on("scroll", () =>
        {
            if (windowQuery.scrollTop() >= rightTable.offset().top)
            {
                header.css("position", "fixed")
                    .css("right", "14px")
                    .css("left", "auto");
            }
            else
            {
                header.css("position", "relative");
            }
        });
    }

    private setupMobileScrollBehavior()
    {
        if (!Helper.isMobileScreen())
        {
            return;
        }

        const tableElement = this.rightTable[0];
        let startX = 0, startY = 0;

        const onTouchStart = (e: TouchEvent) =>
        {
            const touch = e.touches[0];
            startX = touch.clientX;
            startY = touch.clientY;
            this.isScrolling = false;
        };

        const onTouchMove = (e: TouchEvent) =>
        {
            const touch = e.touches[0];
            if (!touch)
            {
                return;
            }

            const dx = touch.clientX - startX;
            const dy = touch.clientY - startY;
            const movedEnough = dx * dx + dy * dy > this.scrollThreshold ** 2;

            if (movedEnough)
            {
                this.isScrolling = true;
                this.deselectActiveCell();
            }
        };

        const onTouchEnd = () =>
        {
            // Reset after small delay to avoid accidental re-taps
            setTimeout(() => (this.isScrolling = false), 100);
        };

        tableElement.addEventListener("touchstart", onTouchStart, { passive: true });
        tableElement.addEventListener("touchmove", onTouchMove, { passive: true });
        tableElement.addEventListener("touchend", onTouchEnd, { passive: true });
    }
    private deselectActiveCell()
    {
        if (!this.hot)
        {
            return;
        }

        this.hot.deselectCell();
        const activeElement = document.activeElement as HTMLElement;
        if (activeElement && activeElement.blur)
        {
            activeElement.blur();
        }

    }

    private attachKeyListener(): void
    {
        const container = this.rightTable?.[0];
        if (!container)
        {
            return;
        }

        if (this.boundKeyHandler)
        {
            container.removeEventListener("keydown", this.boundKeyHandler);
        }

        this.boundKeyHandler = (e: KeyboardEvent) =>
        {
            if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "e")
            {
                e.preventDefault();
                this.handleEditShortcut();
            }
        };

        container.addEventListener("keydown", this.boundKeyHandler);
    }

    destroy(): void
    {
        if (this.boundKeyHandler && this.rightTable?.[0])
        {
            this.rightTable[0].removeEventListener("keydown", this.boundKeyHandler);
        }
        this.hot?.destroy();
    }

    private handleEditShortcut(): void
    {
        if (!this.hot || !this.dataManager)
        {
            return;
        }

        const selected = this.hot.getSelectedLast();
        if (!selected)
        {
            return;
        }

        const [row, col] = selected;
        const cellData = this.dataManager.getData().getCell(row, col);
        if (!cellData || !cellData.EditFormUrl)
        {
            MessageDisplayer.showWarning(Txt.noEditFormAvailable);
            return;
        }

        const form = new Form(cellData.EditFormUrl);
        form.onDataChanged = id =>
        {
            this.dataManager.reloadCellData(
                {
                    startRow: row,
                    endRow: row,
                    startColumn: col,
                    endColumn: col,
                    isSingleCell: () => true
                },
                null,
                id
            );
        };
        FormDisplayer.openInModal(form);
        this.deselectActiveCell();
    }
}
